import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Flame, Share2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { inviteLink } from "@/lib/referral";

// A one-tap, post-worthy image card for users who've reached Founding Member — turns the
// achievement into fresh invites. Renders a branded card + QR and exports it as a PNG to share.
export function FoundingShareCard({ code, qualified }) {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const link = inviteLink(code);
  const handle = user?.username ? `@${user.username}` : user?.display_name || "A BLOQ founder";

  const shareCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#07070B" });
      const text = `I'm a Founding Member on BLOQ Talk — the trust-gated Web3 messenger. Join with my link and we both get 7 days of BLOQ+ free: ${link}`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "bloqtalk-founding.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "BLOQ Talk — Founding Member" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "bloqtalk-founding.png";
        a.click();
        try {
          await navigator.clipboard.writeText(link);
        } catch {
          /* ignore */
        }
        toast.success("Card saved — link copied. Post it anywhere!");
      }
    } catch {
      toast.error("Couldn't generate the card — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid="founding-share-card">
      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-3xl border border-bloq-mint/40 bg-gradient-to-br from-[#0b1a17] via-bloq-bg to-[#0d0a1e] p-6"
        data-testid="founding-card-visual"
      >
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg font-black tracking-tight text-white">
            BLOQ<span className="text-bloq-mint">Talk</span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bloq-gradient px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-bloq-bg">
            <Flame className="h-3 w-3" /> Founding Member
          </span>
        </div>
        <div className="mt-6 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm text-bloq-muted">I'm a Founding Member of</div>
            <div className="font-heading text-2xl font-bold leading-tight text-white">BLOQ Talk</div>
            <div className="mt-1 truncate text-sm font-semibold text-white/90">{handle}</div>
            <div className="mt-1 text-xs text-bloq-mint">{qualified}+ verified friends · lifetime 50% off BLOQ+</div>
          </div>
          <div className="shrink-0 rounded-xl bg-white p-2">
            <QRCodeCanvas value={link} size={76} bgColor="#ffffff" fgColor="#07070B" level="M" />
          </div>
        </div>
        <div className="mt-5 rounded-xl border border-white/10 bg-bloq-bg/50 px-3 py-2 text-center text-xs text-white/90">
          Join with <span className="font-mono font-bold text-bloq-mint">{code}</span> — we both get 7 days of BLOQ+ free
        </div>
      </div>
      <button
        onClick={shareCard}
        disabled={busy}
        className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bloq-gradient text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60"
        data-testid="founding-share-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share my Founding card
      </button>
    </div>
  );
}
